/**
 * Typed HTTP client for the platform API.
 *
 * Request and response shapes come from `schema.d.ts`, which is generated from
 * the server's OpenAPI document. Changing an endpoint therefore breaks the
 * client at compile time rather than at runtime in front of a customer.
 */
import type { paths } from './schema';

export type { paths };

/** Reads a response body of the given path and method. */
// 200 or 201: an endpoint that creates something answers with the second, and a
// caller should not have to know which in order to name the type it gets back.
export type ResponseOf<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
    responses: { 200: { content: { 'application/json': infer R } } };
}
    ? R
    : paths[P][M] extends { responses: { 201: { content: { 'application/json': infer C } } } }
      ? C
      : never;

/**
 * Reads the JSON body an operation takes, whether the contract marks it required or
 * optional. An endpoint whose body is optional — one that took none before it
 * learned to take one — still hands a caller the shape to send, rather than
 * `never`, which typed every such call as an error.
 */
export type RequestOf<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
    requestBody?: infer B;
}
    ? NonNullable<B> extends { content: { 'application/json': infer R } }
        ? R
        : never
    : never;

/**
 * A problem+json error from the API (RFC 7807).
 *
 * The server never returns a raw stack trace or driver message. `detail` is
 * English, for integrators and logs; a screen words the refusal by its `code`
 * (ADR 0005), never by `detail`.
 */
export interface ApiProblem {
    /** `urn:problem:<module>.<reason>` for a refusal a person can cause. */
    type?: string;
    title: string;
    status: number;
    detail?: string;
    errors?: Array<{ location?: string; message?: string; value?: unknown }>;
}

const PROBLEM_TYPE_PREFIX = 'urn:problem:';

export class ApiError extends Error {
    readonly status: number;
    readonly problem: ApiProblem;

    constructor(status: number, problem: ApiProblem) {
        super(problem.detail || problem.title || `Request failed with status ${status}`);
        this.name = 'ApiError';
        this.status = status;
        this.problem = problem;
    }

    /**
     * The refusal's stable code — `catalog.sku_taken` — or undefined for a
     * problem that carries none. Screens word this, never `message`, which is
     * the API's English detail.
     */
    get code(): string | undefined {
        const type = this.problem.type;
        return type?.startsWith(PROBLEM_TYPE_PREFIX) ? type.slice(PROBLEM_TYPE_PREFIX.length) : undefined;
    }

    /** The session is gone or was never established. */
    get isUnauthenticated(): boolean {
        return this.status === 401;
    }

    /** Authenticated, but not allowed to do this.  */
    get isForbidden(): boolean {
        return this.status === 403;
    }

    /** Per-field validation messages, keyed by field name. */
    fieldErrors(): Record<string, string> {
        const out: Record<string, string> = {};
        for (const item of this.problem.errors ?? []) {
            if (!item.location || !item.message) continue;
            const field = item.location.replace(/^body\./, '');
            out[field] = item.message;
        }
        return out;
    }
}

export interface ClientOptions {
    /** Base URL. Empty means same-origin, which is how the panel is served. */
    baseUrl?: string;
    /** Name of the cookie holding the CSRF token. */
    csrfCookieName?: string;
    /** Called whenever the API reports the session is no longer valid. */
    onUnauthenticated?: () => void;
    /** How long a GET or HEAD may wait for its answer, in milliseconds. */
    readTimeoutMs?: number;
    /** How long any other request may wait for its answer, in milliseconds. */
    writeTimeoutMs?: number;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const READ_METHODS = new Set(['GET', 'HEAD']);

// A request nobody answers never settles, so the screen stays loading and
// TanStack Query's retry never starts. A read gives up after 30 s and is retried.
// A write waits past the API's own HTTP_WRITE_TIMEOUT (60 s, config.go): giving
// up while the server can still answer would leave the person unsure whether it
// was saved, and a second click could save it twice.
const DEFAULT_READ_TIMEOUT_MS = 30_000;
const DEFAULT_WRITE_TIMEOUT_MS = 70_000;

// The unauthenticated auth endpoints demand a proof of work: before posting to
// them the client fetches a challenge and finds a nonce whose hash clears a
// difficulty bar. It is a fraction of a second for an honest sign-in and a real
// cost for a script firing thousands.
const PROOF_OF_WORK_PATHS = new Set(['/v1/auth/login', '/v1/auth/register']);

export class ApiClient {
    private readonly baseUrl: string;
    private readonly csrfCookieName: string;
    private readonly onUnauthenticated?: () => void;
    private readonly readTimeoutMs: number;
    private readonly writeTimeoutMs: number;

    constructor(options: ClientOptions = {}) {
        this.baseUrl = options.baseUrl ?? '';
        this.csrfCookieName = options.csrfCookieName ?? 'csrf';
        this.onUnauthenticated = options.onUnauthenticated;
        this.readTimeoutMs = options.readTimeoutMs ?? DEFAULT_READ_TIMEOUT_MS;
        this.writeTimeoutMs = options.writeTimeoutMs ?? DEFAULT_WRITE_TIMEOUT_MS;
    }

    async request<T>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
        const headers = new Headers(init.headers);
        headers.set('Accept', 'application/json');

        // A file is sent as itself. Passing one through JSON.stringify produces
        // "{}" — a request that succeeds, uploads nothing, and gives no hint why.
        const isRaw = isBinary(body);
        if (body !== undefined && !isRaw) {
            headers.set('Content-Type', 'application/json');
        }
        if (isRaw && !headers.has('Content-Type') && body instanceof Blob && body.type) {
            headers.set('Content-Type', body.type);
        }

        // The session cookie rides along automatically, so it proves nothing about
        // intent on its own. Echoing the CSRF cookie in a header is the half a
        // cross-site page cannot perform.
        if (!SAFE_METHODS.has(method.toUpperCase())) {
            const token = readCookie(this.csrfCookieName);
            if (token) headers.set('X-CSRF-Token', token);
        }

        // Solve the bot-guard challenge for the endpoints that require one. A server
        // with the guard disabled answers difficulty zero, so this is a no-op there.
        if (method.toUpperCase() === 'POST' && PROOF_OF_WORK_PATHS.has(path)) {
            const proof = await solveProofOfWork(this.baseUrl);
            if (proof) headers.set('X-Proof-Of-Work', proof);
        }

        // A signal the caller passes still cancels the request; the timeout is added
        // to it, never in its place.
        const timeout = AbortSignal.timeout(READ_METHODS.has(method.toUpperCase()) ? this.readTimeoutMs : this.writeTimeoutMs);

        const response = await fetch(`${this.baseUrl}${path}`, {
            ...init,
            method,
            headers,
            signal: init.signal ? AbortSignal.any([init.signal, timeout]) : timeout,
            // Required for the session cookie to be sent at all.
            credentials: 'same-origin',
            body: body === undefined ? undefined : isRaw ? (body as BodyInit) : JSON.stringify(body),
        });

        if (response.status === 204) {
            return undefined as T;
        }

        const payload = await response.json().catch(() => null);

        if (!response.ok) {
            const problem: ApiProblem = payload ?? {
                title: response.statusText,
                status: response.status,
            };
            if (response.status === 401) {
                this.onUnauthenticated?.();
            }
            throw new ApiError(response.status, problem);
        }

        return payload as T;
    }

    get<T>(path: string, init?: RequestInit): Promise<T> {
        return this.request<T>('GET', path, undefined, init);
    }

    post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
        return this.request<T>('POST', path, body, init);
    }

    put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
        return this.request<T>('PUT', path, body, init);
    }

    patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
        return this.request<T>('PATCH', path, body, init);
    }

    delete<T>(path: string, init?: RequestInit): Promise<T> {
        return this.request<T>('DELETE', path, undefined, init);
    }
}

/** Whether a body should be sent as bytes rather than encoded as JSON. */
function isBinary(body: unknown): boolean {
    return body instanceof Blob || body instanceof ArrayBuffer || ArrayBuffer.isView(body) || body instanceof FormData;
}

/**
 * Fetches a proof-of-work challenge and returns "<challenge>.<nonce>", or null
 * when the server has the guard disabled or the challenge cannot be fetched —
 * in which case the request proceeds and the server decides whether to accept
 * it. The search is a tight loop over nonces until the hash has enough leading
 * zero bits; at the default difficulty this is well under a second.
 *
 * Exported for the shop's own client, whose sign-in and sign-up stand behind
 * the same guard (ADR 0010): one solver, not a copy per app.
 */
export async function solveProofOfWork(baseUrl = ''): Promise<string | null> {
    let challenge: string;
    let difficulty: number;
    try {
        const res = await fetch(`${baseUrl}/v1/auth/challenge`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            // An unanswered challenge falls through to the catch below like any
            // other failure to fetch one, and the request proceeds without it.
            signal: AbortSignal.timeout(DEFAULT_READ_TIMEOUT_MS),
        });
        if (!res.ok) return null;
        const body = (await res.json()) as { challenge?: string; difficulty?: number };
        challenge = body.challenge ?? '';
        difficulty = body.difficulty ?? 0;
    } catch {
        return null;
    }
    if (!challenge || difficulty <= 0) return null;

    const encoder = new TextEncoder();
    for (let nonce = 0; nonce < Number.MAX_SAFE_INTEGER; nonce++) {
        const digest = sha256(encoder.encode(`${challenge}:${nonce}`));
        if (leadingZeroBits(digest) >= difficulty) {
            return `${challenge}.${nonce}`;
        }
    }
    return null;
}

/** Counts the leading zero bits of a digest, its measured difficulty. */
function leadingZeroBits(bytes: Uint8Array): number {
    let count = 0;
    for (const b of bytes) {
        if (b === 0) {
            count += 8;
            continue;
        }
        count += Math.clz32(b) - 24;
        break;
    }
    return count;
}

// Minimal synchronous SHA-256 (FIPS 180-4). The Web Crypto digest is async;
// running thousands of awaited hashes to solve a challenge would be far slower
// than this tight, allocation-light loop.
const SHA256_K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function sha256(message: Uint8Array): Uint8Array {
    const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);

    const bitLen = message.length * 8;
    // message + 0x80 + zero pad to 56 mod 64 + 8-byte big-endian length
    const withPad = new Uint8Array((((message.length + 8) >> 6) + 1) * 64);
    withPad.set(message);
    withPad[message.length] = 0x80;
    const view = new DataView(withPad.buffer);
    view.setUint32(withPad.length - 4, bitLen >>> 0, false);
    view.setUint32(withPad.length - 8, Math.floor(bitLen / 0x100000000), false);

    const w = new Uint32Array(64);
    for (let offset = 0; offset < withPad.length; offset += 64) {
        for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
        for (let i = 16; i < 64; i++) {
            const w15 = w[i - 15]!;
            const w2 = w[i - 2]!;
            const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
            const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
            w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) | 0;
        }

        let a = h[0]!;
        let b = h[1]!;
        let c = h[2]!;
        let d = h[3]!;
        let e = h[4]!;
        let f = h[5]!;
        let g = h[6]!;
        let hh = h[7]!;
        for (let i = 0; i < 64; i++) {
            const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const t1 = (hh + S1 + ch + SHA256_K[i]! + w[i]!) | 0;
            const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (S0 + maj) | 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) | 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) | 0;
        }
        h[0] = (h[0]! + a) | 0;
        h[1] = (h[1]! + b) | 0;
        h[2] = (h[2]! + c) | 0;
        h[3] = (h[3]! + d) | 0;
        h[4] = (h[4]! + e) | 0;
        h[5] = (h[5]! + f) | 0;
        h[6] = (h[6]! + g) | 0;
        h[7] = (h[7]! + hh) | 0;
    }

    const out = new Uint8Array(32);
    for (let i = 0; i < 8; i++) {
        const word = h[i]!;
        out[i * 4] = (word >>> 24) & 0xff;
        out[i * 4 + 1] = (word >>> 16) & 0xff;
        out[i * 4 + 2] = (word >>> 8) & 0xff;
        out[i * 4 + 3] = word & 0xff;
    }
    return out;
}

function rotr(x: number, n: number): number {
    return (x >>> n) | (x << (32 - n));
}

function readCookie(name: string): string | null {
    const prefix = `${name}=`;
    for (const part of document.cookie.split(';')) {
        const trimmed = part.trim();
        if (trimmed.startsWith(prefix)) {
            return decodeURIComponent(trimmed.slice(prefix.length));
        }
    }
    return null;
}
